import { useEffect } from 'react';
import QueryString from 'query-string';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { FeatureFlags } from 'modules/featureFlags';
import { isKSACountry, isUAECountry } from 'config/countriesEnvironments';
import config from 'config';

// Query string param that overrides the feature flag, both ways:
//   ?redirect-to-v2=true  -> force the redirect (test it before the rollout)
//   ?redirect-to-v2=false -> suppress the redirect (escape to v1 after the rollout)
//   absent                -> follow the region's rollout-redirect-*-to-v2 flag
const REDIRECT_PARAM = 'redirect-to-v2';

const OVERRIDE_STORAGE_KEY = '_lean_redirect_to_v2_override';

// Hyperlink is end-user facing and served from this codebase on the connect
// subdomain (e.g. connect.sa.leantech.me), so it must never be redirected to
// the dashboard.
const isConnectSubdomain = (): boolean =>
  window.location.hostname.split('.')[0] === 'connect';

const readStoredOverride = (): string | null => {
  try {
    return sessionStorage.getItem(OVERRIDE_STORAGE_KEY);
  } catch {
    return null;
  }
};

const resolveOverride = (
  fromUrl: string | (string | undefined)[] | null | undefined,
): string | null => {
  if (fromUrl !== 'true' && fromUrl !== 'false') return readStoredOverride();

  try {
    sessionStorage.setItem(OVERRIDE_STORAGE_KEY, fromUrl);
  } catch {
    return fromUrl;
  }

  return fromUrl;
};

/**
 * Frontend-redirects users from v1 (dev portal, dev.leantech.me) to v2
 * (dashboard.leantech.me) when the rollout flag for the build's region is on.
 * UAE and KSA are gated by independent flags; any other region never
 * redirects. The connect (Hyperlink) subdomain is never redirected. Renders nothing.
 */
const V2RedirectGate = (): null => {
  const isUaeRedirectOn = useFeatureIsOn(
    FeatureFlags.rolloutRedirectUaeToV2 as string,
  );
  const isKsaRedirectOn = useFeatureIsOn(
    FeatureFlags.rolloutRedirectKsaToV2 as string,
  );

  // Region is a build-time constant, so resolve the active flag at render.
  let isRedirectFlagOn = false;
  if (isUAECountry) isRedirectFlagOn = isUaeRedirectOn;
  else if (isKSACountry) isRedirectFlagOn = isKsaRedirectOn;

  useEffect(() => {
    if (!isUAECountry && !isKSACountry) return;
    if (isConnectSubdomain()) return;

    const params = QueryString.parse(window.location.search);
    const override = resolveOverride(params[REDIRECT_PARAM]);

    let shouldRedirect = isRedirectFlagOn;
    if (override === 'true') shouldRedirect = true;
    if (override === 'false') shouldRedirect = false;

    if (!shouldRedirect) return;

    // Preserve any existing query params except our own control param.
    const { [REDIRECT_PARAM]: _omit, ...rest } = params;
    const query = QueryString.stringify(rest);
    const target = `${config.v2DashboardUrl}${query ? `?${query}` : ''}`;

    window.location.replace(target);
  }, [isRedirectFlagOn]);

  return null;
};

export default V2RedirectGate;
